import * as React from "react";
import { useMediaQuery, useTheme } from "@mui/material";

/**
 * Where the user's drawer preference is kept. This is a user-visible contract:
 * renaming the key silently resets everybody back to the breakpoint default.
 */
export const CONVERSATION_DRAWER_STORAGE_KEY = "conversationDrawerOpen";

const readStoredPreference = (): boolean | null => {
  try {
    const stored = localStorage.getItem(CONVERSATION_DRAWER_STORAGE_KEY);
    if (stored === "true") {
      return true;
    }
    if (stored === "false") {
      return false;
    }
    return null;
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). "No
    // preference" is a perfectly good answer - fall back to the default.
    return null;
  }
};

const writeStoredPreference = (open: boolean): void => {
  try {
    localStorage.setItem(CONVERSATION_DRAWER_STORAGE_KEY, String(open));
  } catch {
    // Losing the preference is not worth breaking the UI over.
  }
};

/**
 * Open state for the conversation drawer, resolved once per page load.
 *
 * Desktop (lg and up) starts with the drawer out, because the panel shifts
 * over to make room for it and the drawer stays put while you browse. Below
 * md the panel does not move, so an open drawer would cover the conversation -
 * those widths always start closed, and never write a preference that could
 * later follow the user back to a desktop.
 *
 * Only a deliberate open or close, made through the returned setter, is ever
 * remembered. The resolved default is not a choice: saving it would pin a
 * first visit at tablet width as "closed" on every later desktop visit, and
 * would write for visitors who have no drawer at all (this hook runs before
 * ConversationContainer decides what to render).
 *
 * A drop-in replacement for `useState<boolean>`, so call sites pass the tuple
 * straight through as before.
 */
export function useConversationDrawerState(): [
  boolean,
  React.Dispatch<React.SetStateAction<boolean>>,
] {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const [open, setOpen] = React.useState<boolean>(() => {
    if (!isMdUp) {
      return false;
    }
    return readStoredPreference() ?? isLgUp;
  });

  // Read through a ref so that resizing the window on its own neither moves
  // the drawer nor rewrites the preference - only an explicit open/close does.
  const isMdUpRef = React.useRef(isMdUp);
  React.useEffect(() => {
    isMdUpRef.current = isMdUp;
  }, [isMdUp]);

  const userHasChosenRef = React.useRef(false);
  const setOpenByUser = React.useCallback<
    React.Dispatch<React.SetStateAction<boolean>>
  >((value) => {
    userHasChosenRef.current = true;
    setOpen(value);
  }, []);

  React.useEffect(() => {
    if (!userHasChosenRef.current || !isMdUpRef.current) {
      return;
    }
    writeStoredPreference(open);
  }, [open]);

  return [open, setOpenByUser];
}
